import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const ganttTasks = [
  { name: 'Entretien des terrains', category: 'infra', segments: [{ start: 2, end: 2 }] },
  { name: 'Mise en place courts/buvette', category: 'infra', segments: [{ start: 3, end: 3 }] },
  { name: 'Rangement des courts', category: 'infra', segments: [{ start: 9, end: 9 }] },
  { name: 'Interclubs Actifs', category: 'interclub', segments: [{ start: 4, end: 5 }] },
  { name: 'Interclubs Juniors', category: 'interclub', segments: [{ start: 4.5, end: 5 }] },
  { name: 'Cours juniors hiver', category: 'juniors', segments: [{ start: 0, end: 2 }, { start: 10, end: 11 }] },
  { name: 'Cours juniors été', category: 'juniors', segments: [{ start: 3.75, end: 5 }, { start: 8, end: 9 }] },
  { name: 'Tournoi interne juniors', category: 'juniors', segments: [{ start: 5, end: 5 }] },
  { name: 'Renouvellement des licences', category: 'admin', segments: [{ start: 2, end: 2 }, { start: 10, end: 10 }] },
  { name: 'Valider cours hiver J+S', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Inscription tournois internes', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Envoi cotisations annuelles + licences', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Envoi facture cours juniors hiver', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Envoi facture sponsors', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Ouvrir offre J+S', category: 'admin', segments: [{ start: 2, end: 2 }, { start: 8, end: 8 }] },
  { name: 'Inscription équipe amicale de la Broye', category: 'admin', segments: [{ start: 2, end: 2 }] },
  { name: 'Envoi formulaire subside communal', category: 'admin', segments: [{ start: 8, end: 8 }] },
  { name: 'Inscriptions équipes IC juniors', category: 'admin', segments: [{ start: 3, end: 3 }] },
  { name: 'Réservation salle communale', category: 'admin', segments: [{ start: 5, end: 5 }] },
  { name: 'Valider cours été J+S', category: 'admin', segments: [{ start: 9, end: 9 }] },
  { name: 'Envoi facture cours juniors été', category: 'admin', segments: [{ start: 9, end: 9 }] },
  { name: 'Plugin nouvelle saison', category: 'admin', segments: [{ start: 11, end: 11 }] },
  { name: 'IC Inscription des équipes', category: 'admin', segments: [{ start: 11, end: 11 }] },
  { name: 'Assemblée Générale', category: 'event', segments: [{ start: 2, end: 2 }] },
  { name: 'Apéro remerciement Sponsors', category: 'event', segments: [{ start: 8, end: 8 }] },
  { name: 'Souper équipes Interclubs', category: 'event', segments: [{ start: 11, end: 11 }] },
  { name: 'Demander autorisation préfecture', category: 'loto', segments: [{ start: 4.93, end: 4.93 }, { start: 5.17, end: 5.17 }] },
  { name: 'Commander les fonds à la banque', category: 'loto', segments: [{ start: 7.71, end: 7.71 }, { start: 7.94, end: 7.94 }] },
];

const categoryConfig = {
  infra:     { label: 'Infrastructures', bar: 'bg-amber-400', text: 'text-amber-900', badge: 'bg-amber-100 text-amber-700 border-amber-300' },
  interclub: { label: 'Interclubs',      bar: 'bg-red-400',   text: 'text-red-900',   badge: 'bg-red-100 text-red-700 border-red-300' },
  juniors:   { label: 'Juniors',         bar: 'bg-blue-400',  text: 'text-blue-900',  badge: 'bg-blue-100 text-blue-700 border-blue-300' },
  admin:     { label: 'Administratif',   bar: 'bg-purple-400',text: 'text-purple-900',badge: 'bg-purple-100 text-purple-700 border-purple-300' },
  event:     { label: 'Événements',      bar: 'bg-green-400', text: 'text-green-900', badge: 'bg-green-100 text-green-700 border-green-300' },
  loto:      { label: 'Loto',            bar: 'bg-pink-400',  text: 'text-pink-900',  badge: 'bg-pink-100 text-pink-700 border-pink-300' },
};

const categoryOrder = ['admin', 'infra', 'juniors', 'interclub', 'event', 'loto'];

function TodayMarker({ showLabel = false }) {
  const now = new Date();
  const currentMonth = now.getMonth();
  const dayInMonth = now.getDate();
  const daysInCurrentMonth = new Date(now.getFullYear(), currentMonth + 1, 0).getDate();
  const pct = ((currentMonth + dayInMonth / daysInCurrentMonth) / 12) * 100;

  return (
    <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left: `${pct}%` }}>
      <div className="w-0.5 h-full bg-red-500/60 relative">
        {showLabel && (
          <div className="absolute -top-0 left-1/2 -translate-x-1/2 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-b-md whitespace-nowrap shadow-md">
            Aujourd'hui
          </div>
        )}
      </div>
    </div>
  );
}

function GanttBar({ task, config }) {
  return task.segments.map((seg, i) => {
    const leftPct = (seg.start / 12) * 100;
    const span = (seg.start === seg.end && !Number.isInteger(seg.start)) ? 0.25 : seg.end - seg.start + 1;
    const widthPct = (span / 12) * 100;
    return (
      <div
        key={i}
        className={`absolute top-1.5 bottom-1.5 rounded-md ${config.bar} hover:brightness-110 transition-all shadow-sm`}
        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
        title={`${task.name} — ${MONTHS[Math.floor(seg.start)]}${Math.floor(seg.start) !== Math.floor(seg.end) ? ' à ' + MONTHS[Math.floor(seg.end)] : ''}`}
      />
    );
  });
}

function GridRow({ children, label, isHeader }) {
  return (
    <div className={`flex ${isHeader ? 'bg-gray-50' : 'hover:bg-gray-50/60'} border-b border-gray-100 transition-colors`}>
      <div className={`w-48 lg:w-72 flex-shrink-0 px-4 ${isHeader ? 'py-2' : 'py-3'} border-r border-gray-200 text-sm ${isHeader ? '' : 'text-gray-700 font-medium'}`} title={typeof label === 'string' ? label : ''}>
        {label}
      </div>
      <div className="flex-1 relative min-h-[36px]">
        {/* Month grid lines */}
        <div className="absolute inset-0 flex">
          {MONTHS.map((_, i) => (
            <div key={i} className="flex-1 border-r border-gray-50 last:border-r-0" />
          ))}
        </div>
        {children}
      </div>
    </div>
  );
}

const TimelinePage = () => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (chartRef.current) {
      const now = new Date();
      const pct = (now.getMonth() + now.getDate() / 30) / 12;
      const scrollTarget = chartRef.current.scrollWidth * pct - chartRef.current.clientWidth / 2;
      chartRef.current.scrollLeft = Math.max(0, scrollTarget);
    }
  }, []);

  const groupedTasks = categoryOrder.map(cat => ({
    category: cat,
    config: categoryConfig[cat],
    tasks: ganttTasks.filter(t => t.category === cat),
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <Header scrollY={100} />

      <div className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-green-600 hover:text-green-700 mb-8 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Retour à l'accueil
          </Link>

          <div className="mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
              Planning annuel du comité
            </h1>
            <p className="text-lg text-gray-500">
              Vue d'ensemble des tâches et activités — la ligne rouge indique la date du jour
            </p>
          </div>

          {/* Légende */}
          <div className="flex flex-wrap gap-3 mb-8">
            {categoryOrder.map(cat => (
              <span key={cat} className={`inline-flex items-center text-xs font-semibold px-3 py-1.5 rounded-full border ${categoryConfig[cat].badge}`}>
                {categoryConfig[cat].label}
              </span>
            ))}
          </div>

          {/* Gantt Chart */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
            <div ref={chartRef} className="overflow-x-auto">
              <div className="min-w-[900px]">

                {/* Header */}
                <div className="flex border-b-2 border-gray-200 sticky top-0 bg-white z-30">
                  <div className="w-48 lg:w-72 flex-shrink-0 px-4 py-3 font-bold text-sm text-gray-500 border-r border-gray-200">
                    Tâche / Activité
                  </div>
                  <div className="flex-1 flex relative">
                    {MONTHS.map((m, i) => {
                      const now = new Date();
                      return (
                        <div
                          key={m}
                          className={`flex-1 text-center py-3 text-sm font-bold border-r border-gray-100 last:border-r-0 ${i === now.getMonth() ? 'bg-red-50 text-red-700' : 'text-gray-600'}`}
                        >
                          {m}
                        </div>
                      );
                    })}
                    <TodayMarker showLabel />
                  </div>
                </div>

                {/* Groups */}
                {groupedTasks.map(group => (
                  <div key={group.category}>
                    <GridRow
                      isHeader
                      label={
                        <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full border ${group.config.badge}`}>
                          {group.config.label}
                        </span>
                      }
                    />
                    {group.tasks.map((task, idx) => (
                      <GridRow key={`${group.category}-${idx}`} label={task.name}>
                        <GanttBar task={task} config={group.config} />
                        <TodayMarker />
                      </GridRow>
                    ))}
                  </div>
                ))}

              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TimelinePage;
